import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import Helpers from "../../utils/helpers";
import { useEffect, useRef } from "react";
import { useReactToPrint } from "react-to-print";
import { Button } from "primereact/button";
import { OrderModel } from "../../models/orders/order-list-model";

interface selectedOrderDataProps {
  orderinfo: any;
}

const OrderPrint: React.FC<selectedOrderDataProps> = ({ orderinfo }) => {
  const orderRef = useRef(null);

  const handleOrderPrint = useReactToPrint({
    content: () => orderRef.current,
    documentTitle: 'Order Details',
    pageStyle: `@page { margin-top: 5mm; margin-left: 5mm; margin-right: 3mm; margin-bottom: 1mm; }`
  });

  const formatAddressLine = (parts: (string | undefined)[], separator = ', ') => {
    const validParts = parts.filter(part => part && part.trim() !== '');
    return validParts.join(separator);
  };

  const renderDeliveryAddress = () => {
    const addressParts = [
      orderinfo.address1,
      orderinfo.address2,
      orderinfo.landmark,
      orderinfo.areaName,
      orderinfo.subDistrictName,
      orderinfo.districtName,
      orderinfo.stateName,
      orderinfo.countryName
    ].filter(Boolean);

    return (
      <div className="col">
        <span className="text-primary">
          {formatAddressLine(addressParts)}
        </span><br />
        {orderinfo.zipcode && `Pin: ${orderinfo.zipcode}`}
      </div>
    );
  };


  const priceBodyTemplate = (orderItm: any) => {
    return Helpers.formatAmountInINR(orderItm.unitPrice);
  };

  const quantityBodyTemplate = (orderItm: any) => {
    return orderItm.quantity + " (" + orderItm.measurementTypeCode + ")";
  };

  const amountBodyTemplate = (orderItm: any) => {
    return Helpers.formatAmountInINR(orderItm.taxableAmount);
  };

  const taxBodyTemplate = (orderItm: any) => {
    return Helpers.formatAmountInINR(orderItm.taxAmount);
  };

  const totalAmountBodyTemplate = (orderItm: any) => {
    return Helpers.formatAmountInINR(Math.round(orderItm.grossAmount));
  };

  const totalDiscountAmount = orderinfo.discountAmount || 0;

  const headerTemplate = (
    <>
      <div className="grid">
        <div className="col-6">
          <div className="grid mb-3">
            <img src={process.env.PUBLIC_URL + "/assests/images/DesiriLogo.png"} width={100} />
          </div>
          {
            orderinfo?.orderInvoice?.[0]?.pos &&  // Ensure pos exists in the first orderInvoice item
            <div>
              <div className="grid">
                <div className="col">From:<br /> <span className="text-primary">Desiri Naturals Pvt Ltd</span></div>
              </div>
              <div className="grid">
                {orderinfo.orderInvoice[0].pos.address1 &&
                  <div className="col">
                    <span className="text-primary">
                      {orderinfo.orderInvoice[0].pos.address1}
                      {orderinfo.orderInvoice[0].pos.address2 && `, ${orderinfo.orderInvoice[0].pos.address2}`}
                      {orderinfo.orderInvoice[0].pos.landmark && `, ${orderinfo.orderInvoice[0].pos.landmark}`}
                      {orderinfo.orderInvoice[0].pos.cityName && `, ${orderinfo.orderInvoice[0].pos.cityName}`}
                      {orderinfo.orderInvoice[0].pos.stateName && `, ${orderinfo.orderInvoice[0].pos.stateName}`}
                    </span><br />
                    Pin: <span className="text-primary">{orderinfo.orderInvoice[0].pos.zipcode}</span>
                  </div>
                }
              </div>
            </div>
          }


          <div className="grid">
            <div className="col">To:<br /> <span className="text-primary">{orderinfo.userName}</span></div>
          </div>
          <div className="grid">
            <div className="col">Delivery Address:<br />
            {renderDeliveryAddress()}
            </div>
          </div>
          <div className="grid">
            <div className="col">Mobile #:  <span className="text-primary">{orderinfo.primaryContactNbr}</span></div>
          </div>
          {orderinfo.gsT_Number !== null && <div className="grid">
            <div className="col">GST No:  <span className="text-primary">{orderinfo.gsT_Number}</span></div>
          </div>}
        </div>
        <div className="col-1">
        </div>
        <div className="col-5">
          <div className="grid mt-7">
            <div className="col">Order #: <span className="text-primary"> {orderinfo.orderID}</span></div>
          </div>
          <div className="grid">
            <div className="col">Order Date: <span className="text-primary"> {new Date(orderinfo.createdDate).toLocaleDateString("en-gb",
              {
                day: "2-digit",
                month: "short",
                year: "numeric"
              })}</span>
            </div>
          </div>
          <div className="grid">
            <div className="col">Order Amount: <span className="text-primary">{Helpers.formatAmountInINR(orderinfo.grossAmount)}</span></div>
          </div>
          <div className="grid">
            <div className="col">Payment Mode: <span className="text-primary"> {Helpers.formatPaymentMode(orderinfo.paymentModeName)}</span></div>
          </div>
        </div>
      </div>
    </>
  );
  const footerTemplate = (
    <>
      <div className="grid">
        <div className="col-7">
        </div>
        <div className="col-5">
          <div className="grid">
            <div className="col">Taxable Amount:
              <div className="mr-2" style={{ float: "right" }}>{Helpers.formatAmountInINR(orderinfo.taxableAmount)}</div>
            </div>
          </div>
          <div className="grid">
            <div className="col">Tax Amount:
              <div className="mr-2" style={{ float: "right" }}>{Helpers.formatAmountInINR(orderinfo.taxAmount)}</div>
            </div>
          </div>
          <div className="grid">
            <div className="col">Handling Charges:
              <div className="mr-2" style={{ float: "right" }}>{Helpers.formatAmountInINR(orderinfo.handlingCharges)}</div>
            </div>
          </div>
          <div className="grid">
            <div className="col">Courier Charges:
              <div className="mr-2" style={{ float: "right" }}>{Helpers.formatAmountInINR(orderinfo.courierCharges)}</div>
            </div>
          </div>
          <div className="grid">
            <div className="col">Packing Charges:
              <div className="mr-2" style={{ float: "right" }}>{Helpers.formatAmountInINR(orderinfo.packingCharges)}</div>
            </div>
          </div>
          <div className="grid">
            <div className="col">Payment Gateway Charges:
              <div className="mr-2" style={{ float: "right" }}>{Helpers.formatAmountInINR(orderinfo.paymentGatewayCharges)}</div>
            </div>
          </div>
          {
            totalDiscountAmount > 0 ?
            <div className="grid">
            <div className="col">Discount Amount:
              <div className="mr-2" style={{ float: "right" }}>{Helpers.formatAmountInINR(totalDiscountAmount)}</div>
            </div>
          </div>
          : ""
          }
          <div className="grid">
            <div className="col">Total Amount:
              <div className="mr-2 text-primary" style={{ float: "right" }}>{Helpers.formatAmountInINR(Math.round(totalDiscountAmount > 0 ? orderinfo.grossAmount - totalDiscountAmount : orderinfo.grossAmount))}</div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
  return (
    <>
      {/* <div className="grid">
        <div className="col-11"></div>
        <div className="col-1"> */}
      <Button icon="pi pi-print" style={{ float: "right", marginRight: 2 }} onClick={handleOrderPrint} rounded text severity="info" aria-label="User" />
      {/* </div>        
      </div> */}
      <div ref={orderRef} style={{ overflowX: 'hidden' }}>
        <DataTable
          value={orderinfo.orderDetail} className="no-flex"
          header={headerTemplate} footer={footerTemplate} size="small"
        >
          <Column field="productName" header="Name" />
          <Column field="unitPrice" header="Price" body={priceBodyTemplate} />
          <Column field="quantity" header="Quantity" body={quantityBodyTemplate} />
          <Column header="Amount" body={amountBodyTemplate} />
          <Column header="Tax" body={taxBodyTemplate} />
          <Column field="totalAmount" header="Total" body={totalAmountBodyTemplate} />
        </DataTable>
      </div>
    </>
  );
};

export default OrderPrint;
